import { create } from 'zustand';
import type {
  PreconditionBundle,
  PreconditionStageEntry,
  PreconditionStatus,
} from '@sr-test-wizard/shared';
import { preconditionsApi } from '../lib/preconditionsApi';

interface PreconditionsState {
  runId: string | null;
  bundle: PreconditionBundle | null;
  loading: boolean;
  pulling: boolean;
  pushing: boolean;
  /** itemId being auto-verified right now (one at a time, but a Set keeps the UI honest). */
  verifying: Set<string>;
  /** True while a bulk "verify all" run is in flight. */
  verifyingAll: boolean;
  /** Last "verify all" summary, surfaced in the panel toolbar. */
  lastVerifyAllSummary: {
    total: number;
    done: number;
    todo: number;
    skipped: number;
    manual: number;
    failed: number;
    elapsedMs: number;
  } | null;
  error: string | null;
  /** Active item in the right-pane detail view. */
  selectedId: string | null;
  /** WS-driven dirty flag so the panel knows to re-fetch quietly after another tab edits the stage. */
  dirty: boolean;

  load: (runId: string, options?: { force?: boolean }) => Promise<void>;
  pull: (runId: string) => Promise<void>;
  stageItem: (
    runId: string,
    itemId: string,
    patch: PreconditionStageEntry,
  ) => Promise<void>;
  setStatus: (
    runId: string,
    itemId: string,
    status: PreconditionStatus,
  ) => Promise<void>;
  setNote: (runId: string, itemId: string, note: string | null) => Promise<void>;
  verify: (runId: string, itemId: string) => Promise<void>;
  verifyAll: (runId: string) => Promise<void>;
  push: (runId: string) => Promise<{ pushed: number; tpKey: string }>;
  select: (itemId: string | null) => void;
  markDirty: () => void;
  clear: () => void;
}

/**
 * Preconditions store. The on-disk bundle is the source of truth; the
 * store mirrors it 1:1 plus a few UI-only flags (loading/pulling/etc).
 *
 * Stage edits are persisted server-side after every PATCH so reloads keep
 * pending changes - the user can stage 10 items today, close the tab, and
 * push them tomorrow. The store doesn't optimistic-update on stage edits;
 * the server PATCH is cheap and the response is the new merged bundle, so
 * we just swap the bundle on success.
 */
export const usePreconditionsStore = create<PreconditionsState>((set, get) => ({
  runId: null,
  bundle: null,
  loading: false,
  pulling: false,
  pushing: false,
  verifying: new Set<string>(),
  verifyingAll: false,
  lastVerifyAllSummary: null,
  error: null,
  selectedId: null,
  dirty: false,

  async load(runId, options) {
    const force = options?.force ?? false;
    const state = get();
    if (!force && state.bundle && state.runId === runId) return;
    const isDifferentRun = state.runId !== null && state.runId !== runId;
    set({
      runId,
      loading: true,
      error: null,
      ...(isDifferentRun ? { bundle: null, selectedId: null } : {}),
    });
    try {
      const bundle = await preconditionsApi.get(runId);
      set({ bundle, dirty: false });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async pull(runId) {
    set({ pulling: true, error: null });
    try {
      const bundle = await preconditionsApi.pull(runId);
      set({ bundle, runId, dirty: false });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ pulling: false });
    }
  },

  async stageItem(runId, itemId, patch) {
    try {
      const bundle = await preconditionsApi.stage(runId, itemId, patch);
      set({ bundle, runId });
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  async setStatus(runId, itemId, status) {
    await get().stageItem(runId, itemId, { status });
  },

  async setNote(runId, itemId, note) {
    await get().stageItem(runId, itemId, { note });
  },

  async verify(runId, itemId) {
    set((s) => {
      const next = new Set(s.verifying);
      next.add(itemId);
      return { verifying: next };
    });
    try {
      await preconditionsApi.verify(runId, itemId);
      // Re-fetch the bundle to pick up the new suggestion on the item.
      const bundle = await preconditionsApi.get(runId);
      set({ bundle });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set((s) => {
        const next = new Set(s.verifying);
        next.delete(itemId);
        return { verifying: next };
      });
    }
  },

  async verifyAll(runId) {
    set({ verifyingAll: true, error: null });
    try {
      const { bundle, summary } = await preconditionsApi.verifyAll(runId);
      set({
        bundle,
        lastVerifyAllSummary: {
          total: summary.total,
          done: summary.byStatus.done,
          todo: summary.byStatus.todo,
          skipped: summary.byStatus.skipped,
          manual: summary.byStatus.manual,
          failed: summary.failed,
          elapsedMs: summary.elapsedMs,
        },
      });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ verifyingAll: false });
    }
  },

  async push(runId) {
    set({ pushing: true, error: null });
    try {
      const data = await preconditionsApi.push(runId);
      set({ bundle: data.bundle, dirty: false });
      return { pushed: data.result.pushed, tpKey: data.result.tpKey };
    } catch (err) {
      set({ error: (err as Error).message });
      throw err;
    } finally {
      set({ pushing: false });
    }
  },

  select(itemId) {
    set({ selectedId: itemId });
  },

  markDirty() {
    set({ dirty: true });
  },

  clear() {
    set({
      runId: null,
      bundle: null,
      loading: false,
      pulling: false,
      pushing: false,
      verifying: new Set<string>(),
      verifyingAll: false,
      lastVerifyAllSummary: null,
      error: null,
      selectedId: null,
      dirty: false,
    });
  },
}));
